import type { UserRole } from "@/lib/user-roles"

export const PLAYWRIGHT_AUTH_E2E_HEADER = "x-platform-auth-e2e"
export const PLAYWRIGHT_AUTH_E2E_COOKIE = "platform_auth_e2e_session"
export const SHARED_AUTH_TEST_CODE_PREFIX = "shared-auth-test-"
const SHARED_AUTH_TEST_TOKEN_PREFIX = "shared-auth-test-token:"

export type SharedAuthTestScenario = "onboarding" | "dashboard"

export type SharedIdentity = {
  id?: string
  username?: string | null
  full_name?: string | null
  email?: string | null
  avatar_url?: string | null
  platform_key?: string | null
  roles?: UserRole[]
  openclaw_connected?: boolean
  is_active?: boolean
}

export type LoginErrorCode =
  | "missing_access_token"
  | "identity_sync_failed"
  | "auth_failed"

const LOGIN_ERROR_MESSAGES: Record<LoginErrorCode, string> = {
  missing_access_token:
    "We signed you in, but the session token was missing. Please try again.",
  identity_sync_failed:
    "We could not sync your shared identity. Please try again in a moment.",
  auth_failed: "We could not complete sign-in. Please try again.",
}

export function buildAuthCallbackUrl(origin: string, next?: string | null) {
  const callbackUrl = new URL("/auth/callback", origin)

  if (next && next.startsWith("/")) {
    callbackUrl.searchParams.set("next", next)
  }

  return callbackUrl.toString()
}

export function getLoginErrorMessage(error: string | null) {
  if (!error) {
    return null
  }

  return (
    LOGIN_ERROR_MESSAGES[error as LoginErrorCode] ??
    LOGIN_ERROR_MESSAGES.auth_failed
  )
}

export function isPlaywrightAuthE2EEnabled() {
  return (
    process.env.PLAYWRIGHT_AUTH_E2E === "1" ||
    process.env.NEXT_PUBLIC_PLAYWRIGHT_AUTH_E2E === "1"
  )
}

export function hasPlaywrightAuthE2EHeader(headers: Headers) {
  return headers.get(PLAYWRIGHT_AUTH_E2E_HEADER) === "1"
}

export function getSharedAuthTestScenarioFromCode(
  code: string | null
): SharedAuthTestScenario | null {
  if (!code?.startsWith(SHARED_AUTH_TEST_CODE_PREFIX)) {
    return null
  }

  const scenario = code.slice(SHARED_AUTH_TEST_CODE_PREFIX.length)

  return scenario === "dashboard" || scenario === "onboarding"
    ? scenario
    : null
}

export function getSharedAuthTestScenarioFromCookie(
  value: string | null
): SharedAuthTestScenario | null {
  return value === "dashboard" || value === "onboarding" ? value : null
}

export function buildSharedAuthTestAccessToken(
  scenario: SharedAuthTestScenario
) {
  return `${SHARED_AUTH_TEST_TOKEN_PREFIX}${scenario}`
}

export function getSharedAuthTestScenarioFromAccessToken(
  accessToken: string | null
): SharedAuthTestScenario | null {
  if (!accessToken?.startsWith(SHARED_AUTH_TEST_TOKEN_PREFIX)) {
    return null
  }

  return getSharedAuthTestScenarioFromCookie(
    accessToken.slice(SHARED_AUTH_TEST_TOKEN_PREFIX.length)
  )
}

export function getSharedAuthTestIdentityForScenario(
  scenario: SharedAuthTestScenario
): SharedIdentity {
  if (scenario === "dashboard") {
    return {
      id: "shared-auth-dashboard-user",
      username: "dashboard-user",
      email: "dashboard@example.com",
      platform_key: "platform-dashboard-123",
      roles: ["user"],
      openclaw_connected: false,
      is_active: true,
    }
  }

  return {
    id: "shared-auth-onboarding-user",
    username: "onboarding-user",
    email: "onboarding@example.com",
    platform_key: null,
    roles: ["user"],
    openclaw_connected: false,
    is_active: true,
  }
}

export function getSharedAuthTestIdentity(
  accessToken: string
): SharedIdentity | null {
  const scenario = getSharedAuthTestScenarioFromAccessToken(accessToken)
  return scenario ? getSharedAuthTestIdentityForScenario(scenario) : null
}

export function getSharedAuthTestUser(scenario: SharedAuthTestScenario) {
  const identity = getSharedAuthTestIdentityForScenario(scenario)

  return {
    id: identity.id ?? `shared-auth-${scenario}-user`,
    email: identity.email ?? `${scenario}@example.com`,
    user_metadata: {
      full_name: identity.full_name ?? identity.username ?? scenario,
    },
    app_metadata: {},
    aud: "authenticated",
    role: "authenticated",
    created_at: "2026-04-08T00:00:00.000Z",
  }
}

export function getSharedAuthTestAppUser(scenario: SharedAuthTestScenario) {
  const identity = getSharedAuthTestIdentityForScenario(scenario)

  return {
    id: identity.id ?? `shared-auth-${scenario}-user`,
    platform_key: identity.platform_key ?? "",
    username: identity.username ?? null,
    full_name: identity.full_name ?? null,
    email: identity.email ?? null,
    avatar_url: identity.avatar_url ?? null,
    openclaw_connected: identity.openclaw_connected ?? false,
    is_active: identity.is_active ?? true,
    created_at: "2026-04-08T00:00:00.000Z",
  }
}

export function readSharedAuthTestScenarioFromDocumentCookie() {
  if (typeof document === "undefined") {
    return null
  }

  const cookieValue = document.cookie
    .split("; ")
    .find((part) => part.startsWith(`${PLAYWRIGHT_AUTH_E2E_COOKIE}=`))
    ?.split("=")[1]

  return getSharedAuthTestScenarioFromCookie(cookieValue ?? null)
}
